import React, { useEffect, useState } from "react";
import { X, Plus, Check, ChevronDown } from "lucide-react";

const DISMISS_KEY = "mt_pwa_dismiss_until";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // include iPadOS desktop UA (Macintosh + touch points)
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  const iPadOSDesktop =
    ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
  return (iOSUA || iPadOSDesktop) && !window.MSStream;
}

function isSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // Safari but not Chrome/CriOS/FxiOS/EdgiOS/OPiOS
  return (
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium/.test(ua)
  );
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch (_e) {
    return false;
  }
}

function rememberDismiss(days = 14) {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + days * 24 * 60 * 60 * 1000)
    );
  } catch (_e) {
    /* ignore */
  }
}

// Apple iOS Share icon (square + up arrow)
const AppleShareIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 3v12M12 3l-3.5 3.5M12 3l3.5 3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 11v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Visual iOS install guide. Slides up from the bottom on iOS Safari devices,
 * showing three illustrated steps to add Madreterra TV to the home screen.
 *
 * Behaviour:
 *  - Hidden if already running standalone (already installed) or dismissed.
 *  - Auto-shown after 6 s on iOS Safari.
 *  - Manually re-openable via the "Vedi come installare" pill that appears
 *    above the radio player for iOS visitors that previously dismissed.
 */
export const IosInstallGuide = () => {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [previouslyDismissed, setPreviouslyDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    if (!isIos() || !isSafari()) return undefined;

    setEligible(true);
    setPreviouslyDismissed(Boolean(isDismissed()));

    if (isDismissed()) return undefined;

    const t = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    rememberDismiss(14);
    setOpen(false);
    setPreviouslyDismissed(true);
  };

  const reopen = () => {
    setOpen(true);
  };

  if (!eligible) return null;

  return (
    <>
      {/* Re-open pill for users who dismissed earlier */}
      {!open && previouslyDismissed && (
        <button
          type="button"
          onClick={reopen}
          data-testid="ios-install-reopen"
          className="mt-btn fixed left-1/2 -translate-x-1/2 bottom-4 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-xl"
          style={{
            background: "rgba(20,17,13,0.92)",
            color: "var(--mt-amber-bright)",
            border: "1px solid rgba(212,160,91,0.45)",
            backdropFilter: "blur(10px)",
          }}
        >
          <AppleShareIcon size={14} />
          Aggiungi a Home
        </button>
      )}

      {/* Modal-ish bottom sheet */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center px-3 sm:px-6"
          data-testid="ios-install-guide"
          role="dialog"
          aria-modal="true"
          aria-label="Aggiungi MADRETERRA radio alla schermata Home"
        >
          {/* backdrop */}
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi"
            className="absolute inset-0"
            style={{
              background: "rgba(8,6,4,0.65)",
              backdropFilter: "blur(6px)",
            }}
          />

          {/* sheet */}
          <div
            className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden mt-rise"
            style={{
              background:
                "linear-gradient(180deg, rgba(28,24,19,0.98) 0%, rgba(20,17,13,0.98) 100%)",
              border: "1px solid rgba(212,160,91,0.30)",
              boxShadow: "0 -30px 80px rgba(0,0,0,0.6)",
            }}
          >
            {/* close icon */}
            <button
              type="button"
              onClick={close}
              aria-label="Chiudi"
              data-testid="ios-install-close"
              className="mt-btn absolute top-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center"
              style={{
                color: "var(--mt-text-muted)",
                background: "rgba(245,239,228,0.06)",
              }}
            >
              <X size={16} strokeWidth={2.2} />
            </button>

            {/* header */}
            <div className="px-6 sm:px-7 pt-7 pb-4 text-center">
              <div
                className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                style={{
                  background: "rgba(212,160,91,0.16)",
                  color: "var(--mt-amber-bright)",
                  border: "1px solid rgba(212,160,91,0.35)",
                }}
              >
                <AppleShareIcon size={26} />
              </div>
              <h3
                className="font-display text-2xl leading-tight"
                style={{ color: "var(--mt-cream)" }}
              >
                Aggiungi MADRETERRA radio alla Home
              </h3>
              <p
                className="text-sm mt-1.5"
                style={{ color: "var(--mt-text-muted)" }}
              >
                Per averla come app: <strong style={{ color: "var(--mt-cream)" }}>icona dedicata</strong>, schermo intero, parte istantanea.
              </p>
            </div>

            {/* steps */}
            <ol className="px-5 sm:px-6 pb-3 space-y-2.5">
              <Step
                n={1}
                title="Tocca il pulsante Condividi"
                hint="In basso al centro di Safari"
                icon={
                  <AppleShareIcon size={20} />
                }
                accent="amber"
              />
              <Step
                n={2}
                title={"Scorri e tocca \u201CAggiungi a Home\u201D"}
                hint="Nel menu di condivisione"
                icon={<Plus size={20} strokeWidth={2.4} />}
                accent="amber"
              />
              <Step
                n={3}
                title={"Tocca \u201CAggiungi\u201D"}
                hint="In alto a destra. Pronto!"
                icon={<Check size={20} strokeWidth={2.6} />}
                accent="green"
              />
            </ol>

            {/* arrow pointing down to Safari toolbar */}
            <div className="px-6 pb-6 pt-2 flex flex-col items-center">
              <div
                className="text-[11px] uppercase tracking-[0.28em] font-semibold mb-1.5"
                style={{ color: "var(--mt-amber)" }}
              >
                Inizia da qui
              </div>
              <ChevronDown
                size={28}
                strokeWidth={2}
                className="mt-bounce"
                style={{ color: "var(--mt-amber-bright)" }}
              />
              <button
                type="button"
                onClick={close}
                data-testid="ios-install-dismiss"
                className="mt-btn mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
                style={{
                  background: "var(--mt-amber)",
                  color: "#1a1410",
                }}
              >
                Ho capito
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Step = ({ n, title, hint, icon, accent }) => {
  const isGreen = accent === "green";
  return (
    <li className="flex items-start gap-3.5 rounded-2xl p-3 sm:p-3.5"
      style={{
        background: "rgba(245,239,228,0.025)",
        border: "1px solid rgba(245,239,228,0.06)",
      }}
    >
      <div
        className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0 font-bold text-sm"
        style={{
          background: isGreen
            ? "rgba(37,211,102,0.18)"
            : "rgba(212,160,91,0.18)",
          color: isGreen ? "#3ee07e" : "var(--mt-amber-bright)",
          border: isGreen
            ? "1px solid rgba(37,211,102,0.35)"
            : "1px solid rgba(212,160,91,0.35)",
        }}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="font-display text-base leading-tight"
            style={{ color: "var(--mt-cream)" }}
          >
            {title}
          </span>
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-md ml-auto shrink-0"
            style={{
              color: isGreen ? "#3ee07e" : "var(--mt-amber-bright)",
              background: isGreen
                ? "rgba(37,211,102,0.10)"
                : "rgba(212,160,91,0.10)",
              border: isGreen
                ? "1px solid rgba(37,211,102,0.25)"
                : "1px solid rgba(212,160,91,0.25)",
            }}
          >
            {icon}
          </span>
        </div>
        <p
          className="text-xs mt-0.5"
          style={{ color: "var(--mt-text-muted)" }}
        >
          {hint}
        </p>
      </div>
    </li>
  );
};

export default IosInstallGuide;
